import { Button, ButtonGroup } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import type { AbsenceListItem, Enums } from "@msuite/utils/types";
import { Fragment, useState } from "react";
import { client } from "@/hono";
import { useCitadel } from "@/hooks/use-citadel";
import { EditAbsenceModal } from "@/modals/users/edit-absence-modal";

/** Props Interface */
interface AbsenceInboxCardControlsProps {
	absence: Omit<AbsenceListItem, "consumedDays">;
}

export function AbsenceInboxCardControls({ absence }: AbsenceInboxCardControlsProps) {
	/** Hooks */
	const { t } = useTranslation();
	const disclosure = useDisclosure();
	const qc = useQueryClient();
	const { citadel } = useCitadel();

	/** State */
	const [currentLoading, setCurrentLoading] = useState<null | Enums<"absenceState">>(null);

	/** Functions */
	async function handleOnChangeState(state: Enums<"absenceState">) {
		try {
			setCurrentLoading(state);
			const response = await client.users[":userId"].absences[":absenceId"].state.$patch({
				param: { absenceId: absence.absenceId, userId: absence.userId },
				json: { state },
			});
			// A refusal arrives as a response, not a throw; this toasts the server's reason and
			// throws, so the card keeps its state instead of failing silently.
			await responseHandler(response);
			await qc.invalidateQueries({
				predicate: invalidate("absences"),
			});
		} catch (error) {
			console.error(error);
		} finally {
			setCurrentLoading(null);
		}
	}

	/** Computed */
	// Deciding someone else's leave request is the approver's right; the state
	// route rejects anyone else, so a reader of the inbox sees the request
	// without the two verdict buttons.
	const canApproveAbsence = citadel?.can("approve", "Absence") === true;

	/** Render */
	return (
		<Fragment>
			<ButtonGroup
				isAttached={false}
				className="justify-end"
			>
				<div className="flex-1">
					<Button
						size="xxs"
						icon={<Icon name="Edit" />}
						onClick={disclosure.onOpen}
					>
						{t("general.edit")}
					</Button>
				</div>
				{canApproveAbsence && absence.state !== "rejected" && (
					<Button
						size="xxs"
						isLoading={currentLoading === "rejected"}
						isDisabled={currentLoading !== null}
						onClick={() => {
							handleOnChangeState("rejected");
						}}
						icon={
							<Icon
								name="X"
								color="var(--color-red-500)"
							/>
						}
					>
						{t("general.reject")}
					</Button>
				)}
				{canApproveAbsence && absence.state !== "approved" && (
					<Button
						size="xxs"
						isLoading={currentLoading === "approved"}
						isDisabled={currentLoading !== null}
						onClick={() => {
							handleOnChangeState("approved");
						}}
						icon={
							<Icon
								name="Check"
								color="var(--color-emerald-600)"
							/>
						}
					>
						{t("general.approve")}
					</Button>
				)}
			</ButtonGroup>
			{disclosure.isOpen && (
				<EditAbsenceModal
					isOpen={disclosure.isOpen}
					onClose={disclosure.onClose}
					absenceId={absence.id}
				/>
			)}
		</Fragment>
	);
}
